import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { ViewControlGroup } from '@peruselab/structure-editor/components';

/** #807 R4 -- the toolbar's groups that can move into ⋯ (the app's, then the paper's own). */
export type ToolbarGroup =
  | 'align'
  | 'distribute'
  | 'order'
  | 'appearance'
  | 'atom'
  | 'transform'
  | 'pan'
  | 'quickExport'
  | 'quickClear'
  | 'file'
  | 'operations'
  | 'selection'
  | 'modes'
  | 'layout'
  | ViewControlGroup;

/**
 * Lowest priority first: the order groups leave the row when it runs out of room, and the
 * reverse of the order they come back (owner-approved D4). Undo/Redo, Place freely and ⋯ itself
 * never leave: Place freely is held with one finger while the other draws.
 */
export const OVERFLOW_ORDER: readonly ToolbarGroup[] = [
  // Additional Arrange shortcuts yield before any existing control.
  'order',
  'distribute',
  'align',
  'quickExport',
  'quickClear',
  'appearance',
  'transform',
  'page',
  'layout',
  'snapping',
  'file',
  'zoom',
  'pen',
  // #1078 -- the selected atom's controls stay until only the essentials are left.
  'atom',
  'selection',
  'operations',
  'pan',
  'modes'
];

// A group comes back only with this much room to spare, so the row can't flicker at the edge.
const RESTORE_MARGIN_PX = 8;

/**
 * #807 R4 -- measured overflow, not breakpoints: after every render and on every size change,
 * if the row's controls run past its edge the next group in OVERFLOW_ORDER is hidden (one per
 * pass, so the row settles in a few frames); with room to spare for the most recently hidden
 * group, it comes back. Each group's width is remembered from when it was last shown. The row
 * never scrolls sideways and nothing sits under anything else.
 *
 * `rowRef` is the row's control cluster: it fills the row's free width, and its children are
 * the controls (groups carry `data-toolbar-group`).
 */
export function useToolbarOverflow(rowRef: RefObject<HTMLElement | null>): ReadonlySet<ToolbarGroup> {
  const [hidden, setHidden] = useState<readonly ToolbarGroup[]>([]);
  const widths = useRef(new Map<ToolbarGroup, number>());
  // The last group brought back, and the row width at which bringing it back failed (it had to go
  // again at once): it isn't tried again until the row is wider, so the row can't oscillate.
  const lastRestored = useRef<ToolbarGroup | null>(null);
  const blockedBelowWidth = useRef<number | null>(null);
  const [, setResizeTick] = useState(0);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(() => setResizeTick((tick) => tick + 1));
    observer.observe(row);
    return () => observer.disconnect();
  }, [rowRef]);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    // A selection change can remove optional groups. Drop their stale hidden entries so
    // they cannot delay restoring controls that are still present.
    const present = (group: ToolbarGroup) => row.querySelector(`[data-toolbar-group="${group}"]`) !== null;
    const remaining = hidden.filter(present);
    if (remaining.length !== hidden.length) {
      blockedBelowWidth.current = null;
      lastRestored.current = null;
      setHidden(remaining);
      return;
    }
    for (const element of row.querySelectorAll<HTMLElement>('[data-toolbar-group]')) {
      const group = element.dataset.toolbarGroup as ToolbarGroup;
      if (!element.hidden) widths.current.set(group, element.getBoundingClientRect().width);
    }
    // The controls actually laid out on the row: not a hidden group's (no offsetParent), and not
    // inside an open menu or dialog (the ⋯ menu's panel is a fixed-position child of the row).
    const shown = [...row.querySelectorAll<HTMLElement>('button, output, .viewport-divider')].filter(
      (element) => element.offsetParent !== null && element.closest('[role="menu"], [role="dialog"]') === null
    );
    const box = row.getBoundingClientRect();
    const contentRight = shown.reduce(
      (right, element) => Math.max(right, element.getBoundingClientRect().right),
      box.left
    );
    const spare = box.right - contentRight;
    if (spare < -0.5) {
      const next = OVERFLOW_ORDER.find((group) => !hidden.includes(group) && present(group));
      if (next) {
        if (next === lastRestored.current) blockedBelowWidth.current = box.width;
        lastRestored.current = null;
        setHidden([...hidden, next].sort((a, b) => OVERFLOW_ORDER.indexOf(a) - OVERFLOW_ORDER.indexOf(b)));
      }
      return;
    }
    // A contextual group may first appear while already overflowed. Try restoring it once
    // to measure it; the existing failed-restore guard prevents oscillation if it cannot fit.
    const last = hidden.at(-1);
    const blocked = blockedBelowWidth.current !== null && box.width <= blockedBelowWidth.current + 1;
    if (last && !blocked && spare >= (widths.current.get(last) ?? 0) + RESTORE_MARGIN_PX) {
      lastRestored.current = last;
      blockedBelowWidth.current = null;
      setHidden(hidden.slice(0, -1));
    }
  });

  return useMemo(() => new Set(hidden), [hidden]);
}
